import type { BillingInterval, PlanPackage, SubscriptionPlan } from '@/types';

const PACKAGE_DISPLAY_NAMES: Record<string, string> = {
  freemium: 'Starter',
  starter: 'Starter',
  standard: 'Growth',
  growth: 'Growth',
  premium: 'Pro',
  pro: 'Pro',
  scale: 'Scale',
};

export type PlanFeatureBadge = 'new' | 'coming-soon';

export interface ParsedPlanFeature {
  label: string;
  badge?: PlanFeatureBadge;
  isGroupHeader?: boolean;
}

export function getPlanDisplayName(
  plan: Pick<SubscriptionPlan, 'package' | 'name'>
): string {
  const named = plan.name?.trim();
  if (named) return named;
  return (
    PACKAGE_DISPLAY_NAMES[plan.package] ??
    plan.package.charAt(0).toUpperCase() + plan.package.slice(1)
  );
}

export function getPlanPlatformFeePercent(
  plan: Pick<SubscriptionPlan, 'price' | 'package'>
): number {
  const amount = Number.parseFloat(plan.price ?? '0');
  if (!Number.isFinite(amount) || amount <= 0 || plan.package === 'freemium') {
    return 2;
  }
  return 0;
}

export function getBillingIntervalLabel(
  interval?: BillingInterval | null,
  style: 'short' | 'long' = 'short'
): string {
  switch (interval) {
    case 'quarterly':
      return style === 'long' ? 'Quarterly' : 'quarter';
    case 'yearly':
      return style === 'long' ? 'Annually' : 'year';
    case 'monthly':
    default:
      return style === 'long' ? 'Monthly' : 'month';
  }
}

export function parsePlanFeature(feature: string): ParsedPlanFeature {
  const trimmed = feature.trim();
  if (!trimmed) {
    return { label: '' };
  }

  const isGroupHeader = /:$/.test(trimmed) || /^everything in\b/i.test(trimmed);

  const comingSoonMatch = trimmed.match(/\s+(coming soon)\s*$/i);
  if (comingSoonMatch) {
    return {
      label: trimmed.slice(0, comingSoonMatch.index).trim(),
      badge: 'coming-soon',
      isGroupHeader,
    };
  }

  const newMatch = trimmed.match(/\s+(new)\s*$/i);
  if (newMatch) {
    return {
      label: trimmed.slice(0, newMatch.index).trim(),
      badge: 'new',
      isGroupHeader,
    };
  }

  return { label: trimmed, isGroupHeader };
}

export function getPlanCardVariant(
  pkg: PlanPackage
): 'freemium' | 'standard' | 'premium' | 'scale' {
  if (pkg === 'premium' || pkg === 'pro') return 'premium';
  if (pkg === 'scale') return 'scale';
  if (pkg === 'standard' || pkg === 'growth') return 'standard';
  return 'freemium';
}

export function isFreePlan(plan: Pick<SubscriptionPlan, 'price' | 'package'>) {
  const amount = Number.parseFloat(plan.price ?? '0');
  return plan.package === 'freemium' || !amount;
}
